import type { CoreApiModel } from '@lqa-frontend/api-services';

export enum UserStatus {
	active = 'active',
	pending = 'pending',
	deactivated = 'deactivated',
}

export const userStatusDisplayNames: Record<UserStatus, string> = {
	[UserStatus.active]: 'Active',
	[UserStatus.pending]: 'Pending',
	[UserStatus.deactivated]: 'Deactivated',
};

export enum UserRole {
	manager = 'manager',
	user = 'user',
	contributor = 'contributor',
}

export const userRoleDisplayNames: Record<UserRole, string> = {
	[UserRole.manager]: 'Manager',
	[UserRole.user]: 'User',
	[UserRole.contributor]: 'Contributor',
};

export enum UserPropertyLevel {
	client = 'client',
	brand = 'brand',
	region = 'region',
	hotel = 'hotel',
	department = 'department',
	subDepartment = 'subDepartment',
}

export const userPropertyLevelDisplayNames: Record<UserPropertyLevel, string> =
	{
		[UserPropertyLevel.client]: 'Client',
		[UserPropertyLevel.brand]: 'Brand',
		[UserPropertyLevel.region]: 'Region',
		[UserPropertyLevel.hotel]: 'Hotel',
		[UserPropertyLevel.department]: 'Department',
		[UserPropertyLevel.subDepartment]: 'Sub-department',
	};

export enum UserPlatform {
	web = 'web',
	app = 'app',
	all = 'all',
}

export interface UserReference {
	id: number;
	name: string;
}

export interface UserBrandRef extends UserReference {
	client: UserReference | null;
}

export interface UserHotelRef extends UserReference {
	brand: UserBrandRef | null;
}

export interface UserRegionRef extends UserReference {
	brand: UserBrandRef | null;
}

export interface UserDepartmentRef extends CoreApiModel {
	name: string;
	parent: UserReference | null;
}

export interface UserProperty {
	id: number;
	name: string;
	brandId?: number;
	brandName?: string;
	clientId?: number;
	clientName?: string;
	departmentId?: number;
	departmentName?: string;
	availableDepartmentIds?: number[];
}

export interface UserNameSummary {
	names: string[];
	total: number;
	isAll: boolean;
}

export interface UserPlatformSummary {
	web: boolean;
	app: boolean;
	appSeat: boolean;
}

export enum UserReadOnlyReason {
	outsideReach = 'outsideReach',
	masterUser = 'masterUser',
	self = 'self',
}

export interface IUserCapabilities {
	canEdit: boolean;
	readOnlyReason: UserReadOnlyReason | null;
}

export interface IUserSettingsClientOption extends CoreApiModel {
	name: string;
}

export interface IUserSettingsBrandOption extends CoreApiModel {
	name: string;
	client: UserReference;
}

export interface IUserSettingsRegionOption extends CoreApiModel {
	name: string;
	brand: UserReference;
	client: UserReference;
}

export interface IUserSettingsHotelOption extends CoreApiModel {
	name: string;
	brands: UserReference[];
	client: UserReference;
}

export interface UserCollection extends CoreApiModel {
	firstName: string;
	lastName: string;
	workEmail: string;
	personalEmail: string | null;
	phoneNumber: string | null;
	status: UserStatus;
	role: UserRole;
	canManageUsers: boolean;
	propertyLevel: UserPropertyLevel;
	client: UserReference | null;
	clients: UserNameSummary;
	brands: UserNameSummary;
	properties: UserNameSummary;
	platform: UserPlatformSummary;
	capabilities: IUserCapabilities;
}

export interface UserPermissions {
	canManageUsers: boolean;
	canEnterBlockoutDates: boolean;
}

export interface UserNotifications {
	newAssessment: boolean;
	additionalAssessment: boolean;
	competitiveSetUpdates: boolean;
}

export interface UserAppAccess {
	enabled: boolean;
	licensedHotelId: number | null;
}

export interface UserClientAccess {
	level: UserPropertyLevel.client;
	clients: UserReference[];
}

export interface UserBrandAccess {
	level: UserPropertyLevel.brand;
	brands: UserBrandRef[];
}

export interface UserRegionAccess {
	level: UserPropertyLevel.region;
	regions: UserRegionRef[];
}

export interface UserHotelAccess {
	level: UserPropertyLevel.hotel;
	hotels: UserHotelRef[];
}

export interface UserDepartmentAccess {
	level: UserPropertyLevel.department | UserPropertyLevel.subDepartment;
	hotels: UserHotelRef[];
	departments: UserDepartmentRef[];
}

export type UserPropertyAccess =
	| UserClientAccess
	| UserBrandAccess
	| UserRegionAccess
	| UserHotelAccess
	| UserDepartmentAccess;

export interface UserItem extends CoreApiModel {
	firstName: string;
	lastName: string;
	workEmail: string;
	personalEmail: string | null;
	phoneNumber: string | null;
	jobTitle: string | null;
	role: UserRole;
	status: UserStatus;
	permissions: UserPermissions | null;
	notifications: UserNotifications;
	propertyAccess: UserPropertyAccess;
	app: UserAppAccess;
	capabilities: IUserCapabilities;
}

export interface UserPropertyAccessPayload {
	level: UserPropertyLevel;
	clientIds?: number[];
	brandIds?: number[];
	regionIds?: number[];
	hotelIds?: number[];
	departmentIds?: number[];
}

export interface UserAppAccessPayload {
	enabled: boolean;
	licensedHotelId?: number;
}

export interface IUserWritePayload {
	firstName: string;
	lastName: string;
	workEmail: string;
	personalEmail?: string;
	phoneNumber?: string;
	jobTitle?: string;
	role: UserRole;
	clientId: number;
	permissions?: UserPermissions;
	notifications: UserNotifications;
	propertyAccess: UserPropertyAccessPayload;
	app: UserAppAccessPayload;
}
